import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { COLORS, FONTS, SPRING } from "./constants";
import { GridBackground } from "./components/GridBackground";

export const Scene1Title: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Letter-by-letter reveal for "EAKC"
  const letters = ["E", "A", "K", "C"];

  // Subtitle reveal
  const subtitleAppear = spring({
    frame: frame - 40,
    fps,
    config: SPRING.gentle,
  });

  // Tagline reveal
  const taglineAppear = spring({
    frame: frame - 65,
    fps,
    config: SPRING.gentle,
  });

  // Exit fade
  const exitOpacity = interpolate(frame, [100, 120], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Decorative orbiting ring
  const ringRotation = interpolate(frame, [0, 120], [0, 180]);
  const ringScale = spring({ frame: frame - 5, fps, config: SPRING.slow });

  return (
    <AbsoluteFill style={{ opacity: exitOpacity }}>
      <GridBackground opacity={0.1} />

      {/* Orbiting decorative ring */}
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: `translate(-50%, -50%) rotate(${ringRotation}deg) scale(${ringScale})`,
        }}
      >
        <svg width={500} height={500} viewBox="-250 -250 500 500">
          <defs>
            <linearGradient id="ring-grad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={COLORS.accentBlue} stopOpacity={0.3} />
              <stop offset="50%" stopColor={COLORS.accentPurple} stopOpacity={0.1} />
              <stop offset="100%" stopColor={COLORS.accentTeal} stopOpacity={0.3} />
            </linearGradient>
          </defs>
          <ellipse
            rx={220}
            ry={180}
            fill="none"
            stroke="url(#ring-grad)"
            strokeWidth={1.5}
            strokeDasharray="12 8"
          />
          <ellipse
            rx={200}
            ry={160}
            fill="none"
            stroke={COLORS.accentPurple}
            strokeWidth={0.5}
            strokeOpacity={0.2}
          />
        </svg>
      </div>

      {/* Main title: EAKC */}
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -60%)",
          display: "flex",
          gap: 12,
        }}
      >
        {letters.map((letter, i) => {
          const letterAppear = spring({
            frame: frame - i * 8,
            fps,
            config: { damping: 80, mass: 0.6 },
          });

          const scale = interpolate(letterAppear, [0, 1], [0.2, 1]);
          const opacity = interpolate(letterAppear, [0, 1], [0, 1]);
          const y = interpolate(letterAppear, [0, 1], [40, 0]);

          // Each letter gets a slightly different color from the gradient
          const letterColors = [
            COLORS.accentBlue,
            COLORS.accentPurple,
            COLORS.accentTeal,
            COLORS.accentPink,
          ];

          const glowPulse = interpolate(
            Math.sin((frame - i * 5) * 0.06),
            [-1, 1],
            [20, 40],
          );

          return (
            <span
              key={i}
              style={{
                fontSize: 140,
                fontFamily: FONTS.heading,
                fontWeight: 800,
                color: COLORS.textPrimary,
                transform: `scale(${scale}) translateY(${y}px)`,
                opacity,
                textShadow: `0 0 ${glowPulse}px ${letterColors[i]}, 0 0 ${glowPulse * 2}px ${letterColors[i]}50`,
                display: "inline-block",
                letterSpacing: -2,
              }}
            >
              {letter}
            </span>
          );
        })}
      </div>

      {/* Subtitle */}
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: `translate(-50%, 30px)`,
          opacity: interpolate(subtitleAppear, [0, 1], [0, 1]),
        }}
      >
        <span
          style={{
            fontSize: 32,
            fontFamily: FONTS.body,
            fontWeight: 400,
            color: COLORS.textSecondary,
            letterSpacing: 8,
            textTransform: "uppercase",
          }}
        >
          Enterprise AI Knowledge Chat
        </span>
      </div>

      {/* Tagline */}
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: `translate(-50%, 90px)`,
          opacity: interpolate(taglineAppear, [0, 1], [0, 1]),
          display: "flex",
          gap: 30,
        }}
      >
        {["Unify", "Search", "Understand"].map((word, i) => {
          const wordAppear = spring({
            frame: frame - 70 - i * 8,
            fps,
            config: SPRING.snappy,
          });
          const wordColors = [COLORS.accentBlue, COLORS.accentPurple, COLORS.accentTeal];

          return (
            <span
              key={i}
              style={{
                fontSize: 22,
                fontFamily: FONTS.mono,
                fontWeight: 500,
                color: wordColors[i],
                opacity: interpolate(wordAppear, [0, 1], [0, 1]),
                transform: `translateY(${interpolate(wordAppear, [0, 1], [15, 0])}px)`,
                display: "inline-block",
              }}
            >
              {i > 0 && (
                <span style={{ color: COLORS.textMuted, marginRight: 30 }}>·</span>
              )}
              {word}
            </span>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
